<template>
  <!-- プレビュー画面 -->
  <div v-if="photostream">
    <b-overlay
      v-if="!isLoadError"
      :show="true"
      :class="{ 'w-100': true, 'position-relative': true }"
      no-center
      :opacity="0"
    >
      <img
        loading="lazy"
        ref="attachment"
        :style="imgStyle"
        :src="imagePath"
        crossOrigin="anonymous"
        @load="handleImageLoad"
        @error="reloadWrapper"
      />
      <template #overlay>
        <loading-attachment v-if="loading" :fullscreen="false" />
        <img v-else :src="blankGifUrl" class="w-100 dummyCover" />
      </template>
    </b-overlay>
    <div v-else>
      <img src="/img/icon/icon_file_image.svg" @load="onLoadImage" />
    </div>
  </div>
  <!-- 一覧画面の添付ファイル -->
  <div v-else class="w-100 mx-auto">
    <b-overlay
      v-if="!isLoadError"
      :show="true"
      no-center
      class="w-100 h-100"
      :opacity="loading ? 0.85 : 0"
    >
      <img
        loading="lazy"
        ref="attachment"
        class="preload"
        :class="{ 'hide-img': thumbError }"
        crossOrigin="anonymous"
        :src="imagePath"
        @load="handleThumbLoadWrapper"
        @error="reloadThumbWrapper"
      />
      <template #overlay>
        <loading-attachment v-if="loading" :fullscreen="false" />
        <img v-else :src="blankGifUrl" class="w-100 dummyCover" />
      </template>
    </b-overlay>
    <img
      v-else
      src="/img/icon/icon_file_image.svg"
      class="d-flex mx-auto list-icon-size"
      @load="onLoadImage"
    />
    <div v-if="isLoadError" class="file-name">{{ imageFileName }}</div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import LoadingAttachment from "../loading/LoadingAttachment.vue";
import blankGif from "@/assets/blank.gif";

// NOTE: 旧コードでは CustomButton も登録されていたが、テンプレート未使用の
// デッド登録だったため除去（振る舞い不変）。
export default defineComponent({
  components: {
    LoadingAttachment,
  },
  props: {
    photostream: { type: Boolean, default: false },
    isLoadError: { type: Boolean, default: false },
    imagePath: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    imageFileName: { type: String, default: "" },
    allowDownload: { type: Boolean, default: false },
    downloadNote: { type: String, default: "" },
    onLoadImage: { type: Function as PropType<(event: Event) => void>, default: Function },
    reload: { type: Function as PropType<() => void>, default: Function },
    downloadFile: { type: Function as PropType<() => void>, default: Function },
  },
  setup(props) {
    const imgStyle = ref<Record<string, string>>({});
    const thumbError = ref<boolean>(false);

    const blankGifUrl = computed(() => {
      return blankGif;
    });

    /**
     * 画像のwidth/heightからアスペクト比クラスを返す
     * @param width 画像の幅
     * @param height 画像の高さ
     * @returns 'aspect-landscape' | 'aspect-portrait'
     */
    const getAspectClass = (
      width: number,
      height: number
    ): "aspect-landscape" | "aspect-portrait" => {
      if (width >= height) {
        return "aspect-landscape";
      } else {
        return "aspect-portrait";
      }
    };

    /**
     * imgタグのonLoadでnaturalWidth/Heightからアスペクト比クラスをセット
     */
    const handleImageLoad = (event: Event) => {
      const img = event.target as HTMLImageElement;
      if (props.photostream) {
        const maxW = window.innerWidth * 0.98;
        const maxH = window.innerHeight * 0.8;
        const imgW = img.naturalWidth;
        const imgH = img.naturalHeight;
        let displayW = maxW;
        let displayH = maxW * (imgH / imgW);
        if (displayH > maxH) {
          displayH = maxH;
          displayW = maxH * (imgW / imgH);
        }
        imgStyle.value = {
          width: displayW + "px",
          height: displayH + "px",
          maxWidth: "98vw",
          maxHeight: "80vh",
          objectFit: "contain",
          display: "block",
          margin: "auto",
        };
      }
      if (typeof props.onLoadImage === "function") {
        props.onLoadImage(event);
      }
    };

    const reloadWrapper = (): void => {
      imgStyle.value = {
        width: "0px",
        height: "0px",
        maxWidth: "98vw",
        maxHeight: "80vh",
        objectFit: "contain",
        display: "block",
        margin: "auto",
      };
      props.reload();
    };

    const handleThumbLoadWrapper = (event: Event) => {
      thumbError.value = false;
      if (typeof props.onLoadImage === "function") {
        props.onLoadImage(event);
      }
    };

    const reloadThumbWrapper = (): void => {
      thumbError.value = true;
      if (typeof props.reload === "function") {
        props.reload();
      }
    };

    return {
      imgStyle,
      thumbError,
      blankGifUrl,
      getAspectClass,
      handleImageLoad,
      reloadWrapper,
      handleThumbLoadWrapper,
      reloadThumbWrapper,
    };
  },
});
</script>

<style lang="scss" scoped>
.loading {
  min-height: 140px;
  min-width: 140px;
}
.preload {
  width: 100%;
  height: 100%;
}

.default-tmb {
  display: grid;
  justify-items: center;
  align-items: center;
  width: 178px;
  height: 178px;
  margin-bottom: 8px;
  border-radius: 8px;
  position: relative;
  background-color: white;
}
.file-name {
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  color: #707070;
}
.attention-label {
  padding: 8px 4px 0 4px;
  color: white;
  font-size: 12px;
}
.dummyCover {
  height: 100%;
  z-index: 1;
}
.pdf-icon {
  width: 59px;
  height: 73px;
  object-fit: cover;
}
.list-icon-size {
  height: 72px;
  width: 60px;
}
.hide-img {
  height: 0px;
  width: 0px;
}
</style>
